// FoundingBadge.js — Dramatic reveal for Founding Explorer status
import React, { useEffect, useState } from 'react';

const css = `
@keyframes fb-burst {
  0%   { transform: scale(0) rotate(-20deg); opacity: 0; }
  60%  { transform: scale(1.15) rotate(5deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
}
@keyframes fb-confetti {
  0%   { transform: translateY(0) rotate(0deg); opacity: 1; }
  100% { transform: translateY(120vh) rotate(720deg); opacity: 0; }
}
@keyframes fb-glow {
  0%,100% { box-shadow: 0 0 30px rgba(255,214,0,0.5), 0 0 60px rgba(255,150,0,0.3); }
  50%      { box-shadow: 0 0 60px rgba(255,214,0,0.9), 0 0 120px rgba(255,150,0,0.6); }
}
@keyframes fb-star-spin {
  0%   { transform: rotate(0deg) scale(1); }
  50%  { transform: rotate(180deg) scale(1.2); }
  100% { transform: rotate(360deg) scale(1); }
}
@keyframes fb-number-pop {
  0%   { transform: scale(0); opacity: 0; }
  70%  { transform: scale(1.3); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes fb-shimmer {
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
}
.fb-badge { animation: fb-burst 0.8s cubic-bezier(0.34,1.56,0.64,1) forwards; }
.fb-glow  { animation: fb-glow 2s ease-in-out infinite; }
.fb-spin  { animation: fb-star-spin 4s linear infinite; }
.fb-number { animation: fb-number-pop 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.5s both; }
.fb-shimmer {
  background: linear-gradient(90deg, #FFD600 0%, #FFF176 40%, #FFD600 50%, #FF9500 60%, #FFD600 100%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: fb-shimmer 2.5s linear infinite;
}
`;

function Confetti() {
  const pieces = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 2}s`,
    duration: `${2 + Math.random() * 2}s`,
    color: ['#FFD600','#FF6B00','#FF2D55','#00C851','#0066FF','#8B2BE2'][i % 6],
    size: `${6 + Math.random() * 8}px`,
    shape: i % 3 === 0 ? '50%' : '2px',
  }));
  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 9999 }}>
      {pieces.map(p => (
        <div key={p.id} style={{
          position: 'absolute', top: '-10px', left: p.left,
          width: p.size, height: p.size,
          background: p.color, borderRadius: p.shape,
          animation: `fb-confetti ${p.duration} ${p.delay} ease-in forwards`,
        }} />
      ))}
    </div>
  );
}

export default function FoundingBadge({ number, childName, onClose }) {
  const [phase, setPhase] = useState(0); // 0=fade-in 1=badge 2=text 3=actions

  useEffect(() => {
    if (!document.getElementById('fb-css')) {
      const s = document.createElement('style');
      s.id = 'fb-css'; s.textContent = css;
      document.head.appendChild(s);
    }
    const t1 = setTimeout(() => setPhase(1), 300);
    const t2 = setTimeout(() => setPhase(2), 900);
    const t3 = setTimeout(() => setPhase(3), 1600);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  const shareText = `I am Founding Explorer #${number} on NeevStar — one of only 100 children in the world to earn this badge! 🌟⚜️ neevstar.com`;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9998,
      background: 'linear-gradient(160deg,#0A0010 0%,#1A0033 50%,#0D0A2E 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Nunito', sans-serif", padding: 24,
      opacity: phase >= 0 ? 1 : 0, transition: 'opacity 0.4s',
    }}>
      <style>{css}</style>
      <Confetti />

      {/* Stars background */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        {Array.from({ length: 30 }, (_, i) => (
          <div key={i} style={{
            position: 'absolute',
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: `${2 + Math.random() * 3}px`,
            height: `${2 + Math.random() * 3}px`,
            borderRadius: '50%',
            background: 'rgba(255,214,0,0.6)',
            animation: `fb-glow ${1.5 + Math.random() * 2}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 2}s`,
          }} />
        ))}
      </div>

      {/* Badge */}
      {phase >= 1 && (
        <div className="fb-badge" style={{ textAlign: 'center', marginBottom: 24, position: 'relative', zIndex: 1 }}>
          {/* Outer glow ring */}
          <div className="fb-glow" style={{
            width: 160, height: 160, borderRadius: '50%',
            background: 'linear-gradient(135deg,#FFD600,#FF9500)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto',
            border: '4px solid rgba(255,255,255,0.3)',
          }}>
            {/* Inner badge */}
            <div style={{
              width: 140, height: 140, borderRadius: '50%',
              background: 'linear-gradient(135deg,#1A0033,#0A0010)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              border: '3px solid rgba(255,214,0,0.5)',
            }}>
              <div className="fb-spin" style={{ fontSize: 48 }}>⭐</div>
              <p style={{ margin: 0, fontSize: 9, fontWeight: 900, color: 'rgba(255,214,0,0.8)', letterSpacing: '0.1em' }}>
                FOUNDING
              </p>
            </div>
          </div>
          {/* ⚜️ symbol */}
          <div style={{ fontSize: 32, marginTop: 8 }}>⚜️</div>
        </div>
      )}

      {/* Text */}
      {phase >= 2 && (
        <div style={{ textAlign: 'center', position: 'relative', zIndex: 1, marginBottom: 24 }}>
          <p style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 800, color: 'rgba(255,214,0,0.7)', letterSpacing: '0.08em' }}>
            CONGRATULATIONS, {(childName || 'EXPLORER').toUpperCase()}!
          </p>
          <h1 style={{ margin: '0 0 12px', fontSize: 28, fontWeight: 900, lineHeight: 1.2 }}>
            <span className="fb-shimmer">You are Founding Explorer</span>
          </h1>
          <div className="fb-number" style={{
            display: 'inline-block',
            background: 'linear-gradient(135deg,#FFD600,#FF9500)',
            borderRadius: 20, padding: '8px 28px', marginBottom: 16,
          }}>
            <span style={{ fontSize: 42, fontWeight: 900, color: '#1A0010' }}>#{number}</span>
          </div>
          <p style={{
            margin: 0, fontSize: 14, fontWeight: 700, lineHeight: 1.7,
            color: 'rgba(255,255,255,0.75)', maxWidth: 300,
          }}>
            This badge is <strong style={{ color: '#FFD600' }}>permanent and exclusive</strong> —
            only <strong style={{ color: '#FFD600' }}>100 children</strong> in the world will ever have it.
          </p>
        </div>
      )}

      {/* Actions */}
      {phase >= 3 && (
        <div style={{ width: '100%', maxWidth: 320, position: 'relative', zIndex: 1 }}>
          <button onClick={() => {
            if (navigator.share) navigator.share({ title: 'Founding Explorer!', text: shareText });
            else if (navigator.clipboard) navigator.clipboard.writeText(shareText);
          }} style={{
            width: '100%', padding: '14px', borderRadius: 16, border: 'none', cursor: 'pointer',
            background: 'linear-gradient(135deg,#FFD600,#FF9500)', color: '#1A0010',
            fontWeight: 900, fontSize: 16, fontFamily: "'Nunito', sans-serif", marginBottom: 10,
          }}>📤 Share My Badge!</button>
          <button onClick={onClose} style={{
            width: '100%', padding: '12px', borderRadius: 16, cursor: 'pointer',
            background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)',
            color: 'rgba(255,255,255,0.7)', fontWeight: 700, fontSize: 14,
            fontFamily: "'Nunito', sans-serif",
          }}>Continue to NeevStar →</button>
        </div>
      )}
    </div>
  );
}

// ── Live counter shown on signup page ──────────────────────────
export function FoundingCounter({ count }) {
  const remaining = Math.max(0, 100 - (count || 0));
  const pct = Math.min(100, ((count || 0) / 100) * 100);
  if (remaining <= 0) return null;
  return (
    <div style={{
      background: 'linear-gradient(135deg,rgba(255,214,0,0.12),rgba(255,150,0,0.08))',
      border: '1px solid rgba(255,214,0,0.35)',
      borderRadius: 14, padding: '12px 14px', marginBottom: 16,
      fontFamily: "'Nunito', sans-serif",
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 18 }}>⚜️</span>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 900, color: '#FFD600' }}>
          {count || 0} of 100 Founding Explorer spots claimed
        </p>
      </div>
      <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 6, height: 8, overflow: 'hidden', marginBottom: 6 }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: 'linear-gradient(90deg,#FFD600,#FF9500)',
          borderRadius: 6, transition: 'width 1s ease',
        }} />
      </div>
      <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,214,0,0.7)', fontWeight: 700 }}>
        ⚡ Only {remaining} spot{remaining !== 1 ? 's' : ''} left — sign up now to claim yours!
      </p>
    </div>
  );
}
